import React, { useState, useEffect, useCallback } from "react";
import api, { API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatIDR, formatAUD } from "@/lib/format";
import { PageHeader, Loading } from "@/components/ui-helpers";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, FileSpreadsheet, Printer, Download } from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { key: "trip_pl", label: "Laba Rugi Trip" },
  { key: "sales", label: "Penjualan" },
  { key: "payments", label: "Pembayaran" },
  { key: "customer_balances", label: "Saldo Pelanggan" },
  { key: "purchases", label: "Pembelian" },
  { key: "expenses", label: "Pengeluaran" },
  { key: "deliveries", label: "Pengiriman" },
  { key: "product_status", label: "Status Produk" },
];

function Line({ label, value, strong, tone }) {
  const c = tone === "success" ? "text-[hsl(var(--success))]" : tone === "danger" ? "text-[hsl(var(--danger))]" : "";
  return <div className={`flex justify-between py-1.5 ${strong ? "border-t border-border mt-1 pt-2" : ""}`}><span className={`text-sm ${strong ? "font-medium" : "text-muted-foreground"}`}>{label}</span><span className={`font-mono tabular-nums text-sm ${strong ? "font-semibold" : ""} ${c}`}>{value}</span></div>;
}

export default function Reports() {
  const { selectedTripId } = useApp();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/reports/trip", { params: { trip_id: selectedTripId } }); setReport(r.data); }
    finally { setLoading(false); }
  }, [selectedTripId]);
  useEffect(() => { load(); }, [load]);

  const download = async (reportKey, format) => {
    try {
      const token = localStorage.getItem("jastip_token");
      const params = new URLSearchParams({ report: reportKey, format, ...(selectedTripId !== "all" ? { trip_id: selectedTripId } : {}) });
      const res = await fetch(`${API}/reports/export?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${reportKey}.${format === "excel" ? "xlsx" : format}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Unduhan dimulai");
    } catch { toast.error("Gagal mengekspor"); }
  };

  return (
    <div>
      <PageHeader title="Laporan" subtitle="Laporan keuangan & operasional">
        <Button variant="outline" onClick={() => window.print()} className="no-print"><Printer className="h-4 w-4 mr-1.5" />Cetak</Button>
      </PageHeader>

      {loading ? <Loading /> : report && (
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <p className="font-heading font-medium mb-3">Penjualan</p>
            <Line label="Total Order" value={report.sales.total_orders} />
            <Line label="Produk Terjual" value={report.sales.products_sold} />
            <Line label="Penjualan Kotor" value={formatIDR(report.sales.gross_sales)} />
            <Line label="Pendapatan Jasa Titip" value={formatIDR(report.sales.jasa_titip_revenue)} />
            <Line label="Pendapatan Ongkir" value={formatIDR(report.sales.delivery_revenue)} />
            <Line label="Diskon" value={formatIDR(report.sales.total_discounts)} />
            <Line label="Refund" value={formatIDR(report.sales.refunds)} />
            <Line label="Penjualan Bersih" value={formatIDR(report.sales.net_sales)} strong />
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <p className="font-heading font-medium mb-3">Biaya</p>
            <Line label="Beli (Est AUD)" value={formatAUD(report.cost.est_purchase_aud)} />
            <Line label="Beli (Est IDR)" value={formatIDR(report.cost.est_purchase_idr)} />
            <Line label="Beli (Aktual AUD)" value={formatAUD(report.cost.actual_purchase_aud)} />
            <Line label="Beli (Aktual IDR)" value={formatIDR(report.cost.actual_purchase_idr)} />
            <Line label="Total Pengeluaran" value={formatIDR(report.cost.total_expenses)} strong />
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <p className="font-heading font-medium mb-3">Laba</p>
            <Line label="Laba Margin Produk" value={formatIDR(report.profit.product_margin_profit)} />
            <Line label="Laba Kotor (Est)" value={formatIDR(report.profit.est_gross_profit)} />
            <Line label="Laba Bersih (Est)" value={formatIDR(report.profit.est_net_profit)} tone={report.profit.est_net_profit >= 0 ? "success" : "danger"} />
            <Line label="Laba Kotor (Aktual)" value={formatIDR(report.profit.actual_gross_profit)} />
            <Line label="Laba Bersih (Aktual)" value={formatIDR(report.profit.actual_net_profit)} tone={report.profit.actual_net_profit >= 0 ? "success" : "danger"} strong />
            <Line label="Margin Laba" value={`${report.profit.profit_margin_pct}%`} />
            <Line label="Rata2 Laba/Order" value={formatIDR(report.profit.avg_profit_per_order)} />
            <Line label="Piutang" value={formatIDR(report.profit.outstanding_balance)} tone="danger" />
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-5 shadow-sm no-print">
        <p className="font-heading font-medium mb-4">Ekspor Laporan</p>
        <div className="space-y-2">
          {REPORTS.map((r) => (
            <div key={r.key} className="flex items-center justify-between border-b border-border py-2 last:border-0">
              <span className="text-sm">{r.label}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => download(r.key, "pdf")} data-testid={`export-pdf-${r.key}`}><FileText className="h-3.5 w-3.5 mr-1" />PDF</Button>
                <Button size="sm" variant="outline" onClick={() => download(r.key, "excel")} data-testid={`export-excel-${r.key}`}><FileSpreadsheet className="h-3.5 w-3.5 mr-1" />Excel</Button>
                <Button size="sm" variant="outline" onClick={() => download(r.key, "csv")} data-testid={`export-csv-${r.key}`}><Download className="h-3.5 w-3.5 mr-1" />CSV</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
